import React, { useState, useEffect, useCallback } from 'react'
import { X, Activity, Calendar, Gauge, Layers, Banknote } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { getLocalApiUrl, authFetch } from '../config/api'

const TABS = [
  { key: 'patterns', label: 'Patterns', Icon: Activity },
  { key: 'combined', label: 'Combined summary', Icon: Layers },
  { key: 'sessions', label: 'Sessions', Icon: Calendar },
  { key: 'metering', label: 'Metering', Icon: Gauge },
  { key: 'payouts', label: 'Payouts', Icon: Banknote },
]

function defaultDateRange(days = 90) {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - days)
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) }
}

const fmt$ = (n) => {
  const v = Number(n)
  if (!Number.isFinite(v)) return '—'
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}

const fmtDateTime = (v) => {
  if (!v) return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString()
}

export default function PatronGamingActivityPanel({ memberId, memberName, onClose }) {
  const [activeTab, setActiveTab] = useState('patterns')
  const [dateRange, setDateRange] = useState(() => defaultDateRange(90))
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)

  const [visitPattern, setVisitPattern] = useState(null)
  const [gamingBehaviour, setGamingBehaviour] = useState(null)
  const [combinedRows, setCombinedRows] = useState([])
  const [sessions, setSessions] = useState([])
  const [meterSnapshots, setMeterSnapshots] = useState([])
  const [paysafePayouts, setPaysafePayouts] = useState([])
  const [floorPayouts, setFloorPayouts] = useState([])
  const [payoutsNote, setPayoutsNote] = useState(null)

  const loadData = useCallback(async () => {
    if (!memberId) return
    setLoading(true)
    setLoadError(null)
    setPayoutsNote(null)
    const mid = encodeURIComponent(memberId)
    const { from, to } = dateRange
    const rangeQs = new URLSearchParams()
    if (from) rangeQs.set('from', from)
    if (to) rangeQs.set('to', to)

    try {
      const [
        vpRes,
        gbRes,
        combinedRes,
        sessRes,
        txRes,
        monPayRes,
        floorPayRes,
      ] = await Promise.all([
        authFetch(getLocalApiUrl(`/api/members/${mid}/visit-pattern?months=12`)),
        authFetch(getLocalApiUrl(`/api/members/${mid}/gaming-behaviour?months=12`)),
        authFetch(getLocalApiUrl(`/api/gaming/combined/summary?member_id=${mid}&${rangeQs}&limit=200`)),
        authFetch(getLocalApiUrl(`/api/gaming/sessions?member_id=${mid}&${rangeQs}&limit=200`)),
        authFetch(getLocalApiUrl(`/api/members/${mid}/gaming-transactions?${rangeQs}&limit=200`)),
        authFetch(getLocalApiUrl(`/api/monitoring/payouts?member_id=${mid}&limit=100${from ? `&dateFrom=${from}` : ''}${to ? `&dateTo=${to}` : ''}`)),
        authFetch(getLocalApiUrl(`/api/gaming/payouts?member_id=${mid}&${rangeQs}&limit=100`)),
      ])

      const vpJson = await vpRes.json().catch(() => ({}))
      const gbJson = await gbRes.json().catch(() => ({}))
      const combinedJson = await combinedRes.json().catch(() => ({}))
      const sessJson = await sessRes.json().catch(() => ({}))
      const txJson = await txRes.json().catch(() => ({}))
      const monPayJson = await monPayRes.json().catch(() => ({}))
      const floorPayJson = await floorPayRes.json().catch(() => ({}))

      setVisitPattern(vpJson?.success ? vpJson.data : null)
      setGamingBehaviour(gbJson?.success ? gbJson.data : null)
      setCombinedRows(combinedJson?.success ? combinedJson.data || [] : [])
      setSessions(sessJson?.success ? sessJson.data || [] : [])
      setMeterSnapshots(txJson?.success ? txJson.data || [] : [])

      if (monPayRes.ok && monPayJson?.success) {
        setPaysafePayouts(Array.isArray(monPayJson.data) ? monPayJson.data : [])
      } else {
        setPaysafePayouts([])
        if (monPayRes.status === 403) {
          setPayoutsNote('PaySafe payout records require payout monitoring permission.')
        }
      }

      setFloorPayouts(floorPayJson?.success ? floorPayJson.data || [] : [])

      const partialErrors = []
      if (!combinedRes.ok && combinedJson?.error) partialErrors.push('combined summary')
      if (!sessRes.ok && sessJson?.error) partialErrors.push('sessions')
      if (!txRes.ok && txJson?.error) partialErrors.push('meter snapshots')
      if (partialErrors.length) {
        setLoadError(`Some data could not be loaded: ${partialErrors.join(', ')}`)
      }
    } catch (e) {
      setLoadError(e?.message || 'Failed to load gaming data')
    } finally {
      setLoading(false)
    }
  }, [memberId, dateRange])

  useEffect(() => {
    loadData()
  }, [loadData])

  const visitDow = visitPattern?.has_data
    ? (visitPattern.dow_counts || []).map((d) => ({ d: d.day, n: d.count }))
    : []
  const maxDow = Math.max(...visitDow.map((v) => v.n), 1)

  const topEgms = gamingBehaviour?.has_data
    ? (gamingBehaviour.top_egms || []).slice(0, 5)
    : []

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-psg-teal to-teal-600 text-white">
          <div>
            <h2 className="text-2xl font-bold">Gaming & metering</h2>
            <p className="text-sm opacity-90">{memberName} · Member {memberId}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="px-6 py-3 border-b bg-gray-50 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">From</label>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange((p) => ({ ...p, from: e.target.value }))}
              className="input-field text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">To</label>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange((p) => ({ ...p, to: e.target.value }))}
              className="input-field text-sm"
            />
          </div>
          <Button size="sm" onClick={loadData} disabled={loading}>Apply</Button>
          <span className="text-xs text-gray-500">Patterns use 12-month stats; tables use selected range.</span>
        </div>

        <div className="px-6 border-b flex gap-1 overflow-x-auto">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`px-4 py-3 font-medium border-b-2 transition whitespace-nowrap flex items-center gap-2 ${
                activeTab === key
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
              {key === 'sessions' && sessions.length > 0 && (
                <Badge variant="outline" className="text-xs">{sessions.length}</Badge>
              )}
              {key === 'payouts' && (paysafePayouts.length + floorPayouts.length) > 0 && (
                <Badge variant="outline" className="text-xs">{paysafePayouts.length + floorPayouts.length}</Badge>
              )}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="text-center text-gray-500 py-12">Loading gaming & metering data…</div>
          )}

          {!loading && loadError && (
            <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm mb-4">
              {loadError}
            </div>
          )}

          {!loading && activeTab === 'patterns' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Visit pattern (12 months)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!visitPattern?.has_data ? (
                      <p className="text-sm text-gray-500">No visit-day stats for this member.</p>
                    ) : (
                      <>
                        <div className="flex items-end gap-2 h-32 mb-3">
                          {visitDow.map((v) => (
                            <div key={v.d} className="flex-1 flex flex-col items-center gap-1">
                              <div
                                className="w-full rounded-t bg-psg-teal/70 min-h-[4px]"
                                style={{ height: `${Math.max(4, (v.n / maxDow) * 100)}%` }}
                                title={`${v.d}: ${v.n}`}
                              />
                              <span className="text-xs text-gray-500">{v.d}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          <span>Peak: <strong>{visitPattern.peak_dow}</strong></span>
                          <span>Avg/week: <strong>{visitPattern.avg_visits_per_week}</strong></span>
                          <span>Avg/month: <strong>{visitPattern.avg_visits_per_month}</strong></span>
                          {visitPattern.last_visit_date && (
                            <span>Last visit: <strong>{visitPattern.last_visit_date}</strong></span>
                          )}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Gaming behaviour (12 months)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!gamingBehaviour?.has_data ? (
                      <p className="text-sm text-gray-500">No session behaviour stats.</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-gray-500">Sessions</span><p className="font-semibold">{gamingBehaviour.total_sessions}</p></div>
                        <div><span className="text-gray-500">Avg duration</span><p className="font-semibold">{gamingBehaviour.avg_duration_min != null ? `${gamingBehaviour.avg_duration_min} min` : '—'}</p></div>
                        <div><span className="text-gray-500">Peak arrival</span><p className="font-semibold">{gamingBehaviour.peak_hour_label || '—'}</p></div>
                        <div><span className="text-gray-500">Avg cash in</span><p className="font-semibold">{fmt$(gamingBehaviour.avg_cash_in)}</p></div>
                        <div><span className="text-gray-500">Total credit win</span><p className="font-semibold">{fmt$(gamingBehaviour.total_credit_win)}</p></div>
                        <div><span className="text-gray-500">Multi-session days</span><p className="font-semibold">{gamingBehaviour.multi_session_days ?? '—'}</p></div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {topEgms.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Top EGMs used</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {topEgms.map((e, i) => (
                      <div key={e.egm_pk || i} className="flex items-center gap-3">
                        <span className="font-mono text-sm font-bold w-20">{e.egm_pk}</span>
                        <div className="flex-1 h-2 bg-gray-100 rounded overflow-hidden">
                          <div className="h-full bg-psg-teal rounded" style={{ width: `${e.pct || 0}%` }} />
                        </div>
                        <span className="text-sm text-gray-600 w-24 text-right">{e.session_count} sessions</span>
                        <span className="text-sm font-medium w-20 text-right">{fmt$(e.total_credit_win)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {!loading && activeTab === 'combined' && (
            <div>
              <p className="text-sm text-gray-500 mb-4">
                Session-based summary from enriched combined data (meter × session allocation). Empty until Process & Combine has run on Gaming Data.
              </p>
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Trading day</th>
                      <th className="px-3 py-2">Sessions</th>
                      <th className="px-3 py-2">EGMs</th>
                      <th className="px-3 py-2">Turnover</th>
                      <th className="px-3 py-2">Credit win</th>
                      <th className="px-3 py-2">Cash in</th>
                    </tr>
                  </thead>
                  <tbody>
                    {combinedRows.length === 0 ? (
                      <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">No combined summary rows for this range.</td></tr>
                    ) : (
                      combinedRows.map((r, i) => (
                        <tr key={`${r.TradingDay}-${i}`} className="border-t">
                          <td className="px-3 py-2">{r.TradingDay || '—'}</td>
                          <td className="px-3 py-2">{r.sessions_count ?? '—'}</td>
                          <td className="px-3 py-2">{r.egm_count ?? '—'}</td>
                          <td className="px-3 py-2 font-mono">{fmt$(r.Turnover_alloc)}</td>
                          <td className="px-3 py-2 font-mono">{fmt$(r.CreditWin_alloc)}</td>
                          <td className="px-3 py-2 font-mono">{fmt$(r.CashIn_alloc)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!loading && activeTab === 'sessions' && (
            <div className="overflow-x-auto border rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Start</th>
                    <th className="px-3 py-2">Venue</th>
                    <th className="px-3 py-2">EGM</th>
                    <th className="px-3 py-2">Duration</th>
                    <th className="px-3 py-2">Turnover</th>
                    <th className="px-3 py-2">Credit win</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.length === 0 ? (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">No gaming sessions in this range.</td></tr>
                  ) : (
                    sessions.map((s, idx) => {
                      const start = s.StartTime ? new Date(s.StartTime) : null
                      const end = s.EndTime ? new Date(s.EndTime) : null
                      const durMin = start && end ? Math.round((end - start) / 60000) : null
                      return (
                        <tr key={s.SessionPK || idx} className="border-t hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(s.StartTime)}</td>
                          <td className="px-3 py-2">{s.Venue || '—'}</td>
                          <td className="px-3 py-2 font-mono text-xs">{s.EgmPK || '—'}</td>
                          <td className="px-3 py-2">{durMin != null ? `${durMin} min` : '—'}</td>
                          <td className="px-3 py-2 font-mono">{fmt$(s.Turnover)}</td>
                          <td className="px-3 py-2 font-mono text-green-700">{fmt$(s.CreditWin)}</td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {!loading && activeTab === 'metering' && (
            <div>
              <p className="text-sm text-gray-500 mb-4">EGM meter snapshots at read time (gaming_transactions).</p>
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Time</th>
                      <th className="px-3 py-2">Venue</th>
                      <th className="px-3 py-2">EGM</th>
                      <th className="px-3 py-2">Cash in</th>
                      <th className="px-3 py-2">Cash out</th>
                      <th className="px-3 py-2">Turnover</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meterSnapshots.length === 0 ? (
                      <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">No meter snapshots in this range.</td></tr>
                    ) : (
                      meterSnapshots.map((t, idx) => (
                        <tr key={t.TransactionPK || idx} className="border-t hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(t.SnapShotTime)}</td>
                          <td className="px-3 py-2">{t.Venue || '—'}</td>
                          <td className="px-3 py-2 font-mono text-xs">{t.EgmHouseNo || t.EgmName || '—'}</td>
                          <td className="px-3 py-2 font-mono">{fmt$(t.CashIn)}</td>
                          <td className="px-3 py-2 font-mono">{fmt$(t.CashOut)}</td>
                          <td className="px-3 py-2 font-mono">{fmt$(t.Turnover)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!loading && activeTab === 'payouts' && (
            <div className="space-y-6">
              {payoutsNote && (
                <div className="rounded-md border border-slate-200 bg-slate-50 text-slate-700 px-4 py-3 text-sm">
                  {payoutsNote}
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">PaySafe captured payouts</h3>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2">Time</th>
                        <th className="px-3 py-2">Venue</th>
                        <th className="px-3 py-2">Amount</th>
                        <th className="px-3 py-2">Tier</th>
                        <th className="px-3 py-2">Compliance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paysafePayouts.length === 0 ? (
                        <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-500">No PaySafe payout records for this member.</td></tr>
                      ) : (
                        paysafePayouts.map((p) => (
                          <tr key={p.id || p.payoutId} className="border-t">
                            <td className="px-3 py-2">{fmtDateTime(p.timestamp || p.created_at)}</td>
                            <td className="px-3 py-2">{p.venue || '—'}</td>
                            <td className="px-3 py-2 font-mono font-semibold">{fmt$(p.amount)}</td>
                            <td className="px-3 py-2">{p.payoutTier || p.payout_tier || '—'}</td>
                            <td className="px-3 py-2">
                              <Badge variant="outline" className="text-xs">{p.complianceStatus || p.compliance_status || '—'}</Badge>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Floor cash-outs (gaming meter)</h3>
                <p className="text-xs text-gray-500 mb-2">Cash-out events from gaming_transactions (EGM meter reads).</p>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2">Time</th>
                        <th className="px-3 py-2">Venue</th>
                        <th className="px-3 py-2">EGM</th>
                        <th className="px-3 py-2">Cash out</th>
                        <th className="px-3 py-2">Turnover</th>
                      </tr>
                    </thead>
                    <tbody>
                      {floorPayouts.length === 0 ? (
                        <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-500">No floor cash-out events in this range.</td></tr>
                      ) : (
                        floorPayouts.map((p, idx) => (
                          <tr key={p.TransactionPK || idx} className="border-t">
                            <td className="px-3 py-2">{fmtDateTime(p.SnapShotTime)}</td>
                            <td className="px-3 py-2">{p.Venue || '—'}</td>
                            <td className="px-3 py-2 font-mono text-xs">{p.EgmHouseNo || '—'}</td>
                            <td className="px-3 py-2 font-mono font-semibold text-red-700">{fmt$(p.CashOut)}</td>
                            <td className="px-3 py-2 font-mono">{fmt$(p.Turnover)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
